/**
 * The session domain's FLAG MODULE (ruling 36 as amended 2026-08-29).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/session/flags` publishes both.
 * `/contributions` carries nothing for flags — this domain HAS such a door, and
 * none of the declarations below belongs in it.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from a declaration above it, so the pair cannot drift, and `flags.test.ts`
 * asserts each key appears exactly once in this file and that no read takes a
 * string literal — the only assertion that can tell a descriptor read from a
 * hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard of their own (cache keys do), so this file's source
 * case and `scripts/check-flag-getter-doors.test.ts` are the whole of what
 * stands between a key and a second spelling.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/session/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects. `isTauri` therefore comes from `@heidi/runtime/platform`, the door
 * beside the flags door, rather than from the app's `@/tauri/utils`.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/session/flags`, so its importers and their
 * `jest.mock` literals are untouched.
 *
 * WHAT DOES NOT BELONG HERE — each stay-behind is named rather than silently
 * skipped. Ten were this domain's app-barrel names at the first fan-out;
 * `useIsCopySettingsHintsKilled` is the later one, a raw read on purpose:
 *
 *   - `useIsSessionPageV2Enabled` and `useIsSessionPageV2InviteEnabled` compose
 *     `useLabsFeatureOptIn` / `useIsAutoEnrolledSessionLayoutV2` from the app's
 *     heidi-labs overrides module, and `useIsLabsExperimentalTermsGateEnabled`
 *     gates that settings surface itself. Labs is app-shell.
 *   - `useIsOfflineRecordingEnabled` composes
 *     `useCachedOfflineRecordingFlagFallback`, an app module holding a
 *     localStorage cache the door has no equivalent for.
 *   - `useIsDesktopChronicleEnabled`, `useIsChronicleWifiSyncEnabled`,
 *     `useIsChronicleLocalFirmwareUpgradeEnabled` and
 *     `useIsReleaseRemoteSyncAsyncWebEnabled` gate chronicle and remote-sync
 *     surfaces, which are not session roots — their consumers are
 *     `src/app-shell/chronicle` and `src/hooks/desktop`.
 *   - `useIsAmbientCopilotEnabled` belongs to the copilot root, which the
 *     2026-08-21 roots ruling puts app-shell.
 *   - `useIsSessionUrgencyEnabled` resolves through
 *     `useFeatureFlagEnabled` from `@/features/flags/posthog-compat`, which
 *     reads posthog-js on cloud and only falls back to OpenFeature on the
 *     airgapped build. `@heidi/runtime/feature-flags` carries no PostHog
 *     surface, so moving it would change WHICH PROVIDER answers — a behaviour
 *     change wearing a refactor's clothes, and
 *     `monorepo-wave-1-p3-feature-flags` Z4 is the standing rule against it.
 *   - `useIsCopySettingsHintsKilled` stays a raw read on purpose. Its barrel
 *     docblock says why: the door's dev-only `forceEnableAll` knob would turn
 *     the copy-settings hints off.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
  useBooleanFlagLite,
} from '@heidi/runtime/feature-flags';
import { isTauri } from '@heidi/runtime/platform';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on thirty-eight of the thirty-nine keys, measured rather than
// assumed. The surfaces these gate are the session list and its toolbar, the
// appointment list, the schedule-upload flow, session merge settings, the
// Scribe Canvas experience, the Context tab, and the session-page reads that
// were raw Flagsmith calls — and `rg isOnPrem` over `src/features/sessions`,
// `src/features/canvas`, `src/features/uploadSessionSchedule` and
// `src/features/session-merge-onboarding` returns nothing. Those raw reads
// reached every build. Omitting the field would silently take the D6 default
// (`'cloud-only'`) and withhold the appliance's own session list from it. The
// poor-audio warning stays `cloud-only`: on-prem has no transcript to measure.
// ---------------------------------------------------------------------------

/**
 * Gates the prominent session-list primary action and its supporting toolbar
 * and header layout. This rollout is independent from the Start Now project.
 */
export const sessionListPrimaryActionFlag = defineFeatureFlag({
  key: 'release-scribe-session-list-primary-action',
  defaultValue: false,
  description: 'Session-list primary action and supporting toolbar layout',
  builds: 'all',
});

/** The new session list — table view and consolidated session lists. */
export const newSessionListFlag = defineFeatureFlag({
  key: 'release-scribe-enable-new-session-list',
  defaultValue: false,
  description: 'New session list with table view and consolidated lists',
  builds: 'all',
});

/**
 * Whole-scope status counts for the All Sessions filter. Default off until the
 * scope-aware indexes are live in every region. The raw app read reached every
 * build, so this ships to the appliance too.
 */
export const multiScopeStatusCountsFlag = defineFeatureFlag({
  key: 'release-scribe-multi-scope-status-counts',
  defaultValue: false,
  description: 'Whole-scope status counts for the All Sessions list',
  builds: 'all',
});

/** Stable, unanimated startup sizing for the new session-list split. */
export const stableSessionSplitStartupFlag = defineFeatureFlag({
  key: 'release-scribe-enable-stable-session-split-startup',
  defaultValue: false,
  description: 'Stable initial session-list split sizing on web and desktop',
  builds: 'all',
});

/**
 * THE KEY, PUBLISHED — and derived rather than declared.
 *
 * The app barrel exported this constant before the move and five production
 * modules import it by name to stamp `experiment:` onto their tracking payloads,
 * so it cannot vanish. Re-declaring the literal in the barrel would be a SECOND
 * MINTING SITE, which is the one thing ruling 36 exists to refuse, and neither
 * side's own suite can see that: this file's mint-count case reads only this
 * source, and the app's suite asserts a VALUE, which a copy satisfies. What
 * refuses it is `scripts/check-flag-getter-doors.test.ts` ('a declared key is
 * minted ONCE, on both sides of the door'). So the descriptor above stays the
 * single minting site and this reads `key` off it — the
 * `MEDICATION_HIGHLIGHTS_FLAG_KEY` precedent.
 */
export const NEW_SESSION_LIST_FLAG_KEY = newSessionListFlag.key;

/**
 * Prefetches the session-by-id query on session-row click so the GET overlaps
 * the route transition instead of firing after the page mounts. The value is
 * also attached to `perf.scribe.session_open` tracking so the open-latency arms
 * can be compared in Datadog/RUM.
 */
export const sessionPrefetchFlag = defineFeatureFlag({
  key: 'release-scribe-enable-session-prefetch',
  defaultValue: false,
  description: 'Session-by-id prefetch on session-row click',
  builds: 'all',
});

/** THE KEY, PUBLISHED — same derivation and same reason as the one above. */
export const SESSION_PREFETCH_FLAG_KEY = sessionPrefetchFlag.key;

/**
 * The WEB arm of the Session Merge settings gate. Platform-split so the two
 * ramps are fully decoupled.
 */
export const sessionMergeWebFlag = defineFeatureFlag({
  key: 'release-apps-session-merge-web',
  defaultValue: false,
  description: 'Session Merge settings surface on web',
  builds: 'all',
});

/** The DESKTOP arm of the same gate. */
export const sessionMergeDesktopFlag = defineFeatureFlag({
  key: 'release-apps-session-merge-desktop',
  defaultValue: false,
  description: 'Session Merge settings surface on desktop',
  builds: 'all',
});

/**
 * Schedule Upload — PDF support: the modal and sidebar dropzone accept PDFs in
 * addition to CSV/PNG/JPEG. Gated separately because it depends on a backend
 * rollout.
 */
export const scheduleUploadPdfFlag = defineFeatureFlag({
  key: 'release-scribe-enable-schedule-upload-pdf',
  defaultValue: false,
  description: 'PDF support in the schedule upload flow',
  builds: 'all',
});

/**
 * Emergency kill switch for schedule-sidebar drag-and-drop. When enabled, the
 * sidebar restores the propagation stop that prevents react-dropzone from
 * accepting drag events; browse and paste uploads remain available.
 */
export const scheduleSidebarDragDropKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-schedule-sidebar-drag-drop',
  defaultValue: false,
  description: 'Kill switch for schedule-sidebar drag-and-drop',
  builds: 'all',
});

/**
 * Schedule Upload — context field: each draft session in the upload preview
 * shows an editable "Session context" textarea forwarded to the bulk-create
 * endpoint and stored on `Session.clinician_notes`.
 */
export const scheduleUploadContextFieldFlag = defineFeatureFlag({
  key: 'release-scribe-enable-schedule-upload-context-field',
  defaultValue: false,
  description: 'Session context field in the schedule upload preview',
  builds: 'all',
});

/**
 * UMBRELLA gate for the entire Scribe Canvas flexible-artefact experience:
 * type-into-sections, blank documents, the searchable "+ Create" menu and
 * dictation-as-a-canvas-artefact. Deliberately distinct from the backend
 * rollout flag `release-scribe-flexible-artefact-generation` so the two roll out
 * independently. Rollout/kill gate, NOT an experiment — no `useManualExposure`.
 */
export const scribeCanvasExperienceFlag = defineFeatureFlag({
  key: 'release-scribe-canvas-experience',
  defaultValue: false,
  description: 'Scribe Canvas flexible-artefact experience',
  builds: 'all',
});

/** THE KEY, PUBLISHED — same derivation and same reason as the two above. */
export const SCRIBE_CANVAS_EXPERIENCE_FLAG_KEY = scribeCanvasExperienceFlag.key;

/**
 * Previewing a Context tab attachment: on desktop, clicking a file chip opens
 * the original upload instead of doing nothing. A kill switch for a failing blob
 * fetch or a renderer crash on an unexpected format — OFF restores the previous
 * behaviour and the extracted text stays reachable either way.
 */
export const contextDocumentPreviewFlag = defineFeatureFlag({
  key: 'release-scribe-context-document-preview',
  defaultValue: false,
  description: 'Preview of a Context tab attachment',
  builds: 'all',
});

/** Text and Markdown Context-tab uploads, held behind a rollout kill switch. */
export const textContextDocumentUploadFlag = defineFeatureFlag({
  key: 'release-scribe-text-context-documents',
  defaultValue: false,
  description: 'Text and Markdown uploads in the Context tab',
  builds: 'all',
});

/** Failed context-file chips stay visible, behind a rollout kill switch. */
export const contextUploadFailedChipsFlag = defineFeatureFlag({
  key: 'release-scribe-context-upload-failed-chips',
  defaultValue: false,
  description: 'Failed context-file chips stay visible and deletable',
  builds: 'all',
});

/**
 * Umbrella gate for the "Start now for current sessions" experience: stacked
 * sub-labels, progressive date display, gutter checkboxes, the dedicated actions
 * area, Upcoming in split view, and individuals vs teams views.
 */
export const startNowFlag = defineFeatureFlag({
  key: 'release-scribe-start-now',
  defaultValue: false,
  description: 'Start now for current sessions',
  builds: 'all',
});

/**
 * UMBRELLA gate for the collapsed-Upcoming session-list experience. The single
 * derivation point (`collapsedUpcomingMode` in `use-session-list-query-state`)
 * ANDs this flag with the default date-DESC sort and every surface derives from
 * that one value, so OFF is byte-identical pre-feature behaviour with no half-on
 * state. Rollout/kill gate, NOT an experiment — no `useManualExposure`.
 */
export const collapsedUpcomingSessionsFlag = defineFeatureFlag({
  key: 'release-scribe-collapsed-upcoming-sessions',
  defaultValue: false,
  description: 'Collapsed-Upcoming session-list experience',
  builds: 'all',
});

/**
 * Umbrella rollout gate for the note-gen recovery experience: when note
 * generation fails but the transcript is fine, the app auto-retries and offers
 * regenerate instead of showing a transcript error. Every surface of that
 * experience gates behind this one flag so it ships all-on or all-off. Default
 * OFF ⇒ current behaviour is fully unchanged. `builds: 'all'` because the note
 * error overlay renders on the on-prem appliance too.
 * SCR-4406: the key rename was approved on 2026-10-06 with no alias. Its
 * historical never-deployed premise was contradicted by infra PR #2948's plan;
 * existing flag state and overrides require operator review before deletion.
 */
export const noteGenerationRecoveryFlag = defineFeatureFlag({
  key: 'release-scribe-note-generation-recovery',
  defaultValue: false,
  description: 'Note-gen recovery: auto-retry + regenerate over a safe transcript',
  builds: 'all',
});

/**
 * A FAILED consult note with a saved user edit shows that edit, not the error
 * overlay. Default OFF ships it dark. `builds: 'all'`: the overlay renders on-prem too.
 */
export const failedNoteRescueFlag = defineFeatureFlag({
  key: 'release-scribe-failed-note-rescue',
  defaultValue: false,
  description:
    'FAILED note with a saved edit shows the edit instead of the error overlay',
  builds: 'all',
});

export const sessionListScrollHighlightFlag = defineFeatureFlag({
  key: 'release-scribe-session-list-scroll-highlight',
  defaultValue: false,
  description: 'Session-list hover highlight driven by pointer hit-testing',
  builds: 'all',
});

/**
 * In split view, an unfiltered own-scope session list that settles empty
 * creates and opens a session instead of resting on the empty state. Default
 * OFF ships it dark.
 */
export const createSessionOnEmptyFlag = defineFeatureFlag({
  key: 'release-scribe-create-session-on-empty',
  defaultValue: false,
  description: 'Split view: empty own-scope session list creates and opens a session',
  builds: 'all',
});

/**
 * Session layout V2: visit-guidance surface items (including routine output)
 * render in a "Heidi suggests" card under the Session card, instead of the
 * Tasks card's Routines tab. Default OFF ships it dark. The app gate also
 * requires the Heidi Work umbrella (`useIsV2HeidiSuggestsEnabled`).
 */
export const v2HeidiSuggestsFlag = defineFeatureFlag({
  key: 'release-scribe-v2-heidi-suggests',
  defaultValue: false,
  description: 'Session V2: guidance surface items in a Heidi suggests card',
  builds: 'all',
});

/**
 * Post-recording warning when the transcript carried too little speech for its length.
 * Cloud-only: on-prem has no transcription service transcript to measure.
 */
export const poorAudioWarningFlag = defineFeatureFlag({
  key: 'release-scribe-poor-audio-warning',
  defaultValue: false,
  description: 'Post-recording poor-audio (low speech density) warning banner',
  builds: 'cloud-only',
});

/**
 * Precise session-list name search. Default OFF. The backend reads the same
 * key for the query shape, so toggle it per environment, not by identity.
 */
export const preciseNameSearchFlag = defineFeatureFlag({
  key: 'release-scribe-precise-name-search',
  defaultValue: false,
  description: 'Precise session-list name search',
  builds: 'all',
});

/**
 * Gates the 30s deadline on the session-by-id fetch. Non-hook so
 * `useQuerySessionById`'s `queryFn` can read it without binding the flag as a
 * React value — keeps it out of `queryKey`.
 */
export const sessionFetchTimeoutFlag = defineFeatureFlag({
  key: 'release-scribe-session-fetch-timeout',
  defaultValue: false,
  description: '30s deadline on the session-by-id fetch',
  builds: 'all',
});

/**
 * Note footer cluster — collapse on peek sheet. When enabled, the right-side
 * note footer toggles collapse into icon-only buttons whenever the floating
 * Ask Heidi / Evidence peek sheet is rendered above the input.
 */
export const noteFooterCollapseOnPeekFlag = defineFeatureFlag({
  key: 'release-note-footer-collapse-on-peek',
  defaultValue: false,
  description: 'Note footer toggles collapse to icons when the peek sheet is open',
  builds: 'all',
});

/**
 * Gates the redesigned note-gen loading experience on the live note surface:
 * the editor stays mounted through generation with a floating status row,
 * replacing the full-canvas loading takeover. Applies to every note editor variant.
 */
export const noteGenLoadingV2Flag = defineFeatureFlag({
  key: 'release-scribe-note-gen-loading-v2',
  defaultValue: false,
  description: 'Redesigned note-gen loading experience on the live note surface',
  builds: 'all',
});

/**
 * Kill switch for note-editor HTML escaping. Default OFF: the note editor leaves
 * Ask Heidi / Evidence HTML unescaped so it renders. Flip ON to re-enable
 * angle-bracket escaping if the render fix regresses.
 */
export const noteHtmlEscapeFlag = defineFeatureFlag({
  key: 'release-scribe-note-html-escape',
  defaultValue: false,
  description: 'Re-enable angle-bracket escaping in the note editor',
  builds: 'all',
});

/**
 * Kill switch for the note tab's forced session refetch on mount. When ON the
 * extra GET is skipped; the clobber guards stay on. Default OFF.
 */
export const noteSessionRefetchKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-note-session-refetch',
  defaultValue: false,
  description: 'Kill switch for the note tab forced session refetch on mount',
  builds: 'all',
});

/**
 * Gates the session-page Cmd/Ctrl+A override that scopes Select-All to the
 * active tab pane instead of the whole document. Default OFF restores native
 * browser Select-All.
 */
export const scopedSelectAllFlag = defineFeatureFlag({
  key: 'release-scribe-scoped-select-all',
  defaultValue: false,
  description: 'Scope Select-All to the active session-page tab pane',
  builds: 'all',
});

/**
 * Kill switch for the session page's forced session refetch on mount. When ON
 * the route-remount GET is skipped. Default OFF.
 */
export const sessionMountRefetchKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-session-mount-refetch',
  defaultValue: false,
  description: 'Kill switch for the session page forced session refetch on mount',
  builds: 'all',
});

/**
 * Selects the GA change-management journey for the Session Page V2 invite.
 * OFF keeps the original Labs card; ON selects the GA card. Default OFF.
 */
export const sessionPageV2GaChangeManagementFlag = defineFeatureFlag({
  key: 'release-scribe-session-page-v2-ga-change-management',
  defaultValue: false,
  description: 'GA change-management journey for the Session Page V2 invite',
  builds: 'all',
});

/**
 * Independent rollout for the V1 banner on the Session Page V2 GA invite.
 * Default OFF.
 */
export const sessionPageV2BannerFlag = defineFeatureFlag({
  key: 'release-scribe-session-page-v2-banner',
  defaultValue: false,
  description: 'V1 banner for the Session Page V2 GA invite',
  builds: 'all',
});

/**
 * Kill switch for the session page's forced session refetch on switch. When ON
 * the transition GET is skipped. Default OFF.
 */
export const sessionSwitchRefetchKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-session-switch-refetch',
  defaultValue: false,
  description: 'Kill switch for the session page forced session refetch on switch',
  builds: 'all',
});

/**
 * Gates the stuck-loading sentinel inside the session loading page. When ON,
 * the sentinel emits a Datadog warning while the loading skeleton is mounted.
 * Default OFF.
 */
export const stuckLoadingSentinelFlag = defineFeatureFlag({
  key: 'release-scribe-stuck-loading-sentinel',
  defaultValue: false,
  description: 'Datadog warning while the session loading skeleton stays mounted',
  builds: 'all',
});

/**
 * Master gate for transcript deletion (the delete-transcript UI and any other
 * FE surfaces). Default OFF.
 */
export const transcriptDeletionFlag = defineFeatureFlag({
  key: 'release-scribe-transcript-deletion',
  defaultValue: false,
  description: 'Master gate for transcript deletion',
  builds: 'all',
});

/** Gates Verify Lite in the note footer. Default OFF. */
export const verifyLiteFlag = defineFeatureFlag({
  key: 'release-scribe-verify-lite',
  defaultValue: false,
  description: 'Verify Lite in the note footer',
  builds: 'all',
});

/**
 * Auto-Link Sessions: auto-includes recent sessions from the same patient
 * profile as linked-session context, and surfaces the one-time Auto Pull
 * History spotlight. Distinct from the older linked-sessions auto-select flag.
 */
export const autoLinkSessionsFlag = defineFeatureFlag({
  key: 'release-scribe-auto-link-sessions',
  defaultValue: false,
  description: 'Auto-include recent sessions from the same patient as linked context',
  builds: 'all',
});

/**
 * Kill switch for the "Scribe changed to …" success confirmation toast shown
 * after a scribe switch triggers a note or document regeneration. Defaults ON
 * so the confirmation ships enabled.
 */
export const scribeChangeConfirmationFlag = defineFeatureFlag({
  key: 'release-scribe-change-confirmation',
  defaultValue: true,
  description: 'Success toast after a scribe switch regenerates a note or document',
  builds: 'all',
});

/**
 * When enabled, DOCUMENT templates generate a consult note via the standard
 * note-generation flow instead of the document-generation flow. Non-hook so
 * pure helpers and post-recording callbacks can read it imperatively.
 */
export const generateConsultNoteFromDocumentTemplateFlag = defineFeatureFlag({
  key: 'release-scribe-generate-consult-note-from-document-template',
  defaultValue: false,
  description: 'DOCUMENT templates generate a consult note via note generation',
  builds: 'all',
});

/** Shows the session activity log entry point. Default off. */
export const sessionActivityLogFlag = defineFeatureFlag({
  key: 'release-scribe-session-activity-log',
  defaultValue: false,
  description: 'Session activity log entry point',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off its descriptor.
//
// THE FORCE-ALL COLLAPSE, disclosed per getter rather than in aggregate. Three
// of the app-side bodies these replace carried a hand-rolled
// `process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true'` branch:
// `useIsScribeCanvasExperienceEnabled`, its non-React twin
// `isScribeCanvasExperienceEnabled`, and
// `useIsCollapsedUpcomingSessionsEnabled`. `useBooleanFlag` and `readFlag`
// return `forceEnableAll || value` from the boolean the app injects at
// `src/composition/wiring/runtime-wiring.ts` out of that same env var, so each branch
// COLLAPSES onto the door's own override rather than being dropped. The
// remaining getters GAIN that override, which a raw `useBooleanFlagValue`
// bypassed — the choke point the door exists for (design D8), and a dev-only
// knob. `flags.test.ts` asserts the ABSENCE of `process.env` here, so a later
// edit cannot reinstate a branch by hand.
// ---------------------------------------------------------------------------

/** The session-list primary action and its supporting toolbar layout. */
export const useIsSessionListPrimaryActionEnabled = (): boolean =>
  useBooleanFlag(
    sessionListPrimaryActionFlag.key,
    sessionListPrimaryActionFlag.defaultValue
  );

/**
 * Whole-scope status counts on the All Sessions filter.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsMultiScopeStatusCountsEnabled = (): boolean =>
  useBooleanFlag(multiScopeStatusCountsFlag.key, multiScopeStatusCountsFlag.defaultValue);

/**
 * Precise session-list name search.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsPreciseNameSearchEnabled = (): boolean =>
  useBooleanFlag(preciseNameSearchFlag.key, preciseNameSearchFlag.defaultValue);

/** The new session list — table view and consolidated session lists. */
export const useIsNewSessionListEnabled = (): boolean =>
  // Lite: read from `useSessionsPanelLayout` / `useIsSessionPageV2Enabled`,
  // i.e. by most of the session page — see `useBooleanFlagLite`.
  useBooleanFlagLite(newSessionListFlag.key, newSessionListFlag.defaultValue);

/** Stable startup is an additional rollout gate beneath the new session list. */
export const useIsStableSessionSplitStartupEnabled = (): boolean =>
  useBooleanFlag(
    stableSessionSplitStartupFlag.key,
    stableSessionSplitStartupFlag.defaultValue
  );

/** Session-by-id prefetch on session-row click. */
export const useIsSessionPrefetchEnabled = (): boolean =>
  useBooleanFlag(sessionPrefetchFlag.key, sessionPrefetchFlag.defaultValue);

/**
 * The Session Merge settings surface and its one-time onboarding prompt. Gates
 * ONLY that surface — all other session-merge behaviour is ungated.
 *
 * Both platform reads happen unconditionally, so hook order is stable across
 * renders regardless of `isTauri()`.
 */
export const useIsSessionMergeEnabled = (): boolean => {
  const isWebEnabled = useBooleanFlag(
    sessionMergeWebFlag.key,
    sessionMergeWebFlag.defaultValue
  );
  const isDesktopEnabled = useBooleanFlag(
    sessionMergeDesktopFlag.key,
    sessionMergeDesktopFlag.defaultValue
  );
  return isTauri() ? isDesktopEnabled : isWebEnabled;
};

/** PDF support in the schedule upload flow. */
export const useIsScheduleUploadPdfEnabled = (): boolean =>
  useBooleanFlag(scheduleUploadPdfFlag.key, scheduleUploadPdfFlag.defaultValue);

/** The kill switch for schedule-sidebar drag-and-drop. */
export const useIsScheduleSidebarDragDropKilled = (): boolean =>
  useBooleanFlag(
    scheduleSidebarDragDropKillFlag.key,
    scheduleSidebarDragDropKillFlag.defaultValue
  );

/** The session-context field in the schedule upload preview. */
export const useIsScheduleUploadContextFieldEnabled = (): boolean =>
  useBooleanFlag(
    scheduleUploadContextFieldFlag.key,
    scheduleUploadContextFieldFlag.defaultValue
  );

/** The Scribe Canvas flexible-artefact experience. */
export const useIsScribeCanvasExperienceEnabled = (): boolean =>
  useBooleanFlag(scribeCanvasExperienceFlag.key, scribeCanvasExperienceFlag.defaultValue);

/**
 * The same flag for plain-JS call sites — TipTap extension factories and
 * markdown helpers that are not React components. Ruling 36 (b)'s fetch-time
 * shape: `readFlag` is the door's synchronous read over the same OpenFeature
 * domain client the app-side body resolved directly, so the resolution path is
 * unchanged.
 */
export const isScribeCanvasExperienceEnabled = (): boolean =>
  readFlag(scribeCanvasExperienceFlag.key, scribeCanvasExperienceFlag.defaultValue);

/** Preview of a Context tab attachment. */
export const useIsContextDocumentPreviewEnabled = (): boolean =>
  useBooleanFlag(contextDocumentPreviewFlag.key, contextDocumentPreviewFlag.defaultValue);

/** Text and Markdown Context-tab uploads. */
export const useIsTextContextDocumentUploadEnabled = (): boolean =>
  useBooleanFlag(
    textContextDocumentUploadFlag.key,
    textContextDocumentUploadFlag.defaultValue
  );

/** Failed context-file chips stay visible and deletable. */
export const useIsContextUploadFailedChipsEnabled = (): boolean =>
  useBooleanFlag(
    contextUploadFailedChipsFlag.key,
    contextUploadFailedChipsFlag.defaultValue
  );

/** The "Start now for current sessions" experience. */
export const useIsStartNowEnabled = (): boolean =>
  useBooleanFlag(startNowFlag.key, startNowFlag.defaultValue);

/** The collapsed-Upcoming session-list experience. */
export const useIsCollapsedUpcomingSessionsEnabled = (): boolean =>
  useBooleanFlag(
    collapsedUpcomingSessionsFlag.key,
    collapsedUpcomingSessionsFlag.defaultValue
  );

/** The note-gen recovery experience over a safe transcript. */
export const useIsNoteGenerationRecoveryEnabled = (): boolean =>
  useBooleanFlag(noteGenerationRecoveryFlag.key, noteGenerationRecoveryFlag.defaultValue);

/** The same flag for plain-JS call sites — the stream error path is not a component. */
export const isNoteGenerationRecoveryEnabled = (): boolean =>
  readFlag(noteGenerationRecoveryFlag.key, noteGenerationRecoveryFlag.defaultValue);

/** Show a FAILED note's saved edit instead of the error overlay. */
export const useIsFailedNoteRescueEnabled = (): boolean =>
  useBooleanFlag(failedNoteRescueFlag.key, failedNoteRescueFlag.defaultValue);

/**
 * Pointer-driven session-list hover highlight, a workaround for a stale
 * `:hover` in WebKit. Platform scoping is TARGETING-ONLY — the flag alone
 * controls the feature, so a Flagsmith rule is what narrows it to the intended
 * cohort and there is no code-level platform guard behind it.
 */
export const useIsSessionListScrollHighlightEnabled = (): boolean =>
  useBooleanFlag(
    sessionListScrollHighlightFlag.key,
    sessionListScrollHighlightFlag.defaultValue
  );

/** Create a session when the unfiltered own-scope list settles empty. */
export const useIsCreateSessionOnEmptyEnabled = (): boolean =>
  useBooleanFlag(createSessionOnEmptyFlag.key, createSessionOnEmptyFlag.defaultValue);

/** The V2 "Heidi suggests" card for visit-guidance surface items. */
export const useIsV2HeidiSuggestsFlagEnabled = (): boolean =>
  useBooleanFlag(v2HeidiSuggestsFlag.key, v2HeidiSuggestsFlag.defaultValue);

/** The post-recording poor-audio warning banner. */
export const useIsPoorAudioWarningEnabled = (): boolean =>
  useBooleanFlag(poorAudioWarningFlag.key, poorAudioWarningFlag.defaultValue);

/**
 * The 30s deadline on the session-by-id fetch, for plain-JS call sites.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `getBooleanValue` directly, which bypassed that knob.
 */
export const isSessionFetchTimeoutEnabled = (): boolean =>
  readFlag(sessionFetchTimeoutFlag.key, sessionFetchTimeoutFlag.defaultValue);

/**
 * Note footer toggles collapse to icons when the peek sheet is open.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsNoteFooterCollapseOnPeekEnabled = (): boolean =>
  useBooleanFlag(
    noteFooterCollapseOnPeekFlag.key,
    noteFooterCollapseOnPeekFlag.defaultValue
  );

/**
 * The redesigned note-gen loading experience on the live note surface.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsNoteGenLoadingV2Enabled = (): boolean =>
  useBooleanFlag(noteGenLoadingV2Flag.key, noteGenLoadingV2Flag.defaultValue);

/**
 * Re-enable angle-bracket escaping in the note editor.
 *
 * Gains the door's dev-only `forceEnableAll` override, which turns escaping
 * on. The app body read `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsNoteHtmlEscapeEnabled = (): boolean =>
  useBooleanFlag(noteHtmlEscapeFlag.key, noteHtmlEscapeFlag.defaultValue);

/**
 * Kill switch for the note tab's forced session refetch on mount.
 *
 * Gains the door's dev-only `forceEnableAll` override, which engages this kill.
 * The app body read `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsNoteSessionRefetchKilled = (): boolean =>
  useBooleanFlag(noteSessionRefetchKillFlag.key, noteSessionRefetchKillFlag.defaultValue);

/**
 * Scope Select-All to the active session-page tab pane.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsScopedSelectAllEnabled = (): boolean =>
  useBooleanFlag(scopedSelectAllFlag.key, scopedSelectAllFlag.defaultValue);

/**
 * Kill switch for the session page's forced session refetch on mount.
 *
 * Gains the door's dev-only `forceEnableAll` override, which engages this kill.
 * The app body read `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsSessionMountRefetchKilled = (): boolean =>
  useBooleanFlag(
    sessionMountRefetchKillFlag.key,
    sessionMountRefetchKillFlag.defaultValue
  );

/**
 * The GA change-management journey for the Session Page V2 invite.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsSessionPageV2GaChangeManagementEnabled = (): boolean =>
  useBooleanFlag(
    sessionPageV2GaChangeManagementFlag.key,
    sessionPageV2GaChangeManagementFlag.defaultValue
  );

/**
 * The V1 banner for the Session Page V2 GA invite.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsSessionPageV2BannerEnabled = (): boolean =>
  useBooleanFlag(sessionPageV2BannerFlag.key, sessionPageV2BannerFlag.defaultValue);

/**
 * Kill switch for the session page's forced session refetch on switch.
 *
 * Gains the door's dev-only `forceEnableAll` override, which engages this kill.
 * The app body read `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsSessionSwitchRefetchKilled = (): boolean =>
  useBooleanFlag(
    sessionSwitchRefetchKillFlag.key,
    sessionSwitchRefetchKillFlag.defaultValue
  );

/**
 * The stuck-loading sentinel inside the session loading page.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsStuckLoadingSentinelEnabled = (): boolean =>
  useBooleanFlag(stuckLoadingSentinelFlag.key, stuckLoadingSentinelFlag.defaultValue);

/**
 * The master gate for transcript deletion.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsTranscriptDeletionEnabled = (): boolean =>
  useBooleanFlag(transcriptDeletionFlag.key, transcriptDeletionFlag.defaultValue);

/**
 * Verify Lite in the note footer.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsVerifyLiteEnabled = (): boolean =>
  useBooleanFlag(verifyLiteFlag.key, verifyLiteFlag.defaultValue);

/**
 * Auto-include recent sessions from the same patient as linked context.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsAutoLinkSessionsEnabled = (): boolean =>
  useBooleanFlag(autoLinkSessionsFlag.key, autoLinkSessionsFlag.defaultValue);

/**
 * The "Scribe changed to …" confirmation toast. Default ON.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `useBooleanFlagValue` directly, which bypassed that knob.
 */
export const useIsScribeChangeConfirmationEnabled = (): boolean =>
  useBooleanFlag(
    scribeChangeConfirmationFlag.key,
    scribeChangeConfirmationFlag.defaultValue
  );

/**
 * DOCUMENT templates generate a consult note via the note-generation flow,
 * for plain-JS call sites.
 *
 * Gains the door's dev-only `forceEnableAll` override. The app body read
 * `getBooleanValue` directly, which bypassed that knob.
 */
export const isGenerateConsultNoteFromDocumentTemplateEnabled = (): boolean =>
  readFlag(
    generateConsultNoteFromDocumentTemplateFlag.key,
    generateConsultNoteFromDocumentTemplateFlag.defaultValue
  );

/** Whether the session activity log entry point renders. */
export const useIsSessionActivityLogEnabled = (): boolean =>
  useBooleanFlag(sessionActivityLogFlag.key, sessionActivityLogFlag.defaultValue);
